/**
 * videojs-markers-custom.js
 *
 * Custom replacement for the abandoned videojs-markers 1.0.1 plugin.
 * Written for tawq.in — April 2026 (mh / Claude Sonnet)
 *
 * Uses videojs.registerPlugin() (modern API, compatible with Video.js 7+)
 * instead of the deprecated videojs.plugin() used by the original package.
 *
 * After calling player.markers({ ...options }), the following API is
 * attached directly to the player object to match the old plugin's interface:
 *
 *   player.markers.add([{ time, text, overlayText, currentSpeaker, dur, class, index }])
 *   player.markers.remove(index)
 *   player.markers.removeAll()
 *   player.markers.getMarkers()
 *   player.markers.updateTime()
 *
 * INSTALLATION:
 *   1. Place this file at: tawqin-client/src/plugins/videojs-markers-custom.js
 *   2. In VideoPlayer.vue, replace the three old import lines with:
 *        import "../plugins/videojs-markers-custom.js";
 *   3. The videojs-markers folder and dist files are no longer needed.
 *
 * NOTE: The deploy workflow no longer requires rebuilding the markers dist.
 */

import videojs from 'video.js';

function markersPlugin(options) {
    // `this` is the player instance
    const player = this;

    const settings = Object.assign({
        markers: [],
        markerStyle: { 'background-color': '#86C232' },
        markerTip: {
            display: true,
            text: function(marker) { return marker.text || ''; },
        },
        breakOverlay: { display: false },
        onMarkerReached: null,
        onMarkerClick: null,
    }, options);

    let markers = [];
    let markerEls = {};  // key -> DOM element
    let nextKey = 0;

    // ── Helpers ────────────────────────────────────────────────────────────

    function getProgressBar() {
        return player.controlBar
            && player.controlBar.progressControl
            && player.controlBar.progressControl.seekBar
            && player.controlBar.progressControl.seekBar.el();
    }

    function createMarkerEl(marker) {
        const bar = getProgressBar();
        if (!bar) return;

        const duration = player.duration();
        if (!duration || duration <= 0) return;

        const el = document.createElement('div');
        el.className = 'vjs-marker ' + (marker.class || '');
        el.dataset.markerKey = marker.key;

        // Base positioning styles
        el.style.position = 'absolute';
        el.style.top = '0';
        el.style.bottom = '0';
        el.style.zIndex = '50';
        el.style.cursor = 'pointer';
        el.style.width = '4px';

        // Apply custom markerStyle
        const style = settings.markerStyle || {};
        Object.keys(style).forEach(prop => { el.style[prop] = style[prop]; });

        // Position as % of bar width
        const pct = Math.min((marker.time / duration) * 100, 99.5);
        el.style.left = pct + '%';

        // Tooltip
        if (settings.markerTip && settings.markerTip.display) {
            const tip = document.createElement('div');
            tip.className = 'vjs-marker-tip';
            tip.style.cssText = [
                'position:absolute',
                'bottom:120%',
                'left:50%',
                'transform:translateX(-50%)',
                'background:rgba(0,0,0,0.8)',
                'color:#fff',
                'padding:2px 6px',
                'border-radius:3px',
                'font-size:11px',
                'white-space:nowrap',
                'pointer-events:none',
                'display:none',
                'z-index:100',
            ].join(';');
            const tipText = typeof settings.markerTip.text === 'function'
                ? settings.markerTip.text(marker)
                : (marker.text || '');
            tip.textContent = tipText;
            el.appendChild(tip);
            el.addEventListener('mouseenter', () => { tip.style.display = 'block'; });
            el.addEventListener('mouseleave', () => { tip.style.display = 'none'; });
        }

        // Click
        el.addEventListener('click', (e) => {
            e.stopPropagation();
            let prevent = false;
            if (typeof settings.onMarkerClick === 'function') {
                prevent = settings.onMarkerClick(marker) === false;
            }
            if (!prevent) player.currentTime(marker.time);
        });

        bar.appendChild(el);
        markerEls[marker.key] = el;
    }

    function removeMarkerEl(key) {
        const el = markerEls[key];
        if (el && el.parentNode) el.parentNode.removeChild(el);
        delete markerEls[key];
    }

    function repositionAll() {
        const duration = player.duration();
        if (!duration || duration <= 0) return;
        markers.forEach(marker => {
            const el = markerEls[marker.key];
            if (!el) {
                createMarkerEl(marker); // retry if duration wasn't ready before
            } else {
                const pct = Math.min((marker.time / duration) * 100, 99.5);
                el.style.left = pct + '%';
            }
        });
    }

    function checkMarkerReached() {
        if (typeof settings.onMarkerReached !== 'function') return;
        const currentTime = player.currentTime();
        markers.forEach((marker, index) => {
            if (Math.abs(currentTime - marker.time) < 0.5) {
                settings.onMarkerReached(marker, index);
            }
        });
    }

    // ── Public API — attached directly to player.markers ───────────────────

    const api = {
        add: function(markerList) {
            if (!Array.isArray(markerList)) return;
            markerList.forEach(marker => {
                const key = `tawq-marker-${nextKey++}`;
                marker.key = key;
                markers.push(marker);
                createMarkerEl(marker);
            });
        },

        remove: function(index) {
            if (index < 0 || index >= markers.length) return;
            removeMarkerEl(markers[index].key);
            markers.splice(index, 1);
        },

        removeAll: function() {
            markers.forEach(marker => removeMarkerEl(marker.key));
            markers = [];
        },

        getMarkers: function() {
            return markers;
        },

        updateTime: function() {
            repositionAll();
        },
    };

    // ── Event listeners ────────────────────────────────────────────────────

    player.on('loadedmetadata', repositionAll);
    player.on('timeupdate', checkMarkerReached);
    player.on('dispose', () => { api.removeAll(); });

    // Place any markers passed at init time
    if (settings.markers && settings.markers.length) {
        player.ready(() => api.add(settings.markers));
    }

    // Attach the API to player.markers so callers can do:
    //   this.player.markers.add([...])
    //   this.player.markers.removeAll()
    //   etc.
    player.markers = api;
}

// Register using the modern API — no deprecation warnings
videojs.registerPlugin('markers', markersPlugin);

export default markersPlugin;
